import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowRight, Search, X } from 'lucide-react';

import { Container } from '@/components/ui/container';
import { ArticleArtwork } from '@/components/blog/ArticleArtwork';
import { ArticleCard } from '@/components/blog/ArticleCard';
import { BlogBreadcrumbs } from '@/components/blog/BlogBreadcrumbs';
import { formatArticleDate } from '@/components/blog/formatArticleDate';
import { BookDemoButton } from '@/components/ui/book-demo-dialog';
import { articles } from '@/content/articles';
import {
  blogCategories,
  getBlogCategory,
  type BlogCategory,
} from '@/content/blog-categories';
import { NotFound } from './NotFound';
import '@/components/blog/blog.css';

function BlogIndex({ category }: { category: BlogCategory | undefined }) {
  const [query, setQuery] = useState('');
  const categoryArticles = articles.filter(
    (article) => !category || article.category === category.label
  );
  // Keep category results together below the tabs; only the full index has a hero.
  const featured = category
    ? undefined
    : (categoryArticles.find((article) => article.featured) ??
      categoryArticles[0]);
  const hasSearch = query.trim().length > 0;
  const filtered = categoryArticles.filter((article) =>
    `${article.title} ${article.description}`
      .toLowerCase()
      .includes(query.trim().toLowerCase())
  );
  const visible = hasSearch
    ? filtered
    : filtered.filter((article) => article.slug !== featured?.slug);

  return (
    <>
      <section className="blog-intro pt-32 sm:pt-40">
        <Container>
          <BlogBreadcrumbs category={category?.label} />
          <div className="mt-5 grid gap-6 pb-12 lg:grid-cols-[1.4fr_1fr] lg:items-end lg:gap-16 lg:pb-16">
            <h1 className="max-w-2xl text-[2.8rem] font-medium leading-[1.07] tracking-[-.045em] sm:text-6xl">
              {category ? category.label : 'Insights and updates from Den'}
            </h1>
            <div className="max-w-md lg:pb-1">
              <p className="text-base leading-7 text-ink-500">
                {category
                  ? category.description
                  : 'Practical guides and thoughtful comparisons for teams building, securing, and operating with digital assets.'}
              </p>
            </div>
          </div>
          {featured && !hasSearch && (
            <article className="blog-featured group">
              <Link
                to={`/blog/${featured.slug}`}
                className="grid md:grid-cols-[1.08fr_1fr]"
              >
                <ArticleArtwork
                  article={featured}
                  priority
                  className="blog-featured-art"
                />
                <div className="flex flex-col items-start justify-center p-7 sm:p-10 lg:p-12">
                  <div className="flex flex-wrap items-center gap-3 text-xs font-medium">
                    <span className="blog-eyebrow">FEATURED PERSPECTIVE</span>
                    <span className="text-ink-300">/</span>
                    <span className="text-ink-500">{featured.category}</span>
                  </div>
                  <h2 className="mt-5 text-[1.8rem] font-medium leading-[1.2] tracking-[-.035em] transition-colors group-hover:text-ember-700 lg:text-[2.25rem]">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-sm leading-6 text-ink-500 sm:text-base sm:leading-7">
                    {featured.description}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                    <time dateTime={featured.publishedAt}>
                      {formatArticleDate(featured.publishedAt)}
                    </time>
                    <span aria-hidden="true">·</span>
                    <span>{featured.readingTime} min read</span>
                  </div>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium">
                    Read the article{' '}
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </article>
          )}
        </Container>
      </section>

      <section
        className="pb-20 pt-12 sm:pb-28 sm:pt-16"
        aria-labelledby="articles-heading"
      >
        <Container>
          <h2 id="articles-heading" className="sr-only">
            Browse articles
          </h2>
          <div className="flex flex-col gap-5 border-b border-hairline pb-5 lg:flex-row lg:items-center lg:justify-between">
            <nav
              className="blog-category-nav flex flex-wrap gap-2"
              aria-label="Article categories"
            >
              {[
                { label: 'All articles', href: '/blog' },
                ...blogCategories,
              ].map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-current={
                    (category?.href ?? '/blog') === item.href
                      ? 'page'
                      : undefined
                  }
                  className="blog-filter"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="relative w-full sm:max-w-[250px]">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
              <input
                aria-label="Search articles"
                type="search"
                placeholder="Search articles"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="blog-search"
              />
            </div>
          </div>
          {(category || hasSearch) && (
            <p className="mt-6 text-sm text-ink-500" role="status">
              {visible.length} {visible.length === 1 ? 'article' : 'articles'}
              {category ? ` in ${category.label}` : ''}
              {query.trim() ? ` matching “${query.trim()}”` : ''}
            </p>
          )}
          <div className="mt-10 grid gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-xl font-medium">No articles found.</p>
              <p className="mt-3 text-sm text-ink-500">
                Try another search or explore a different topic.
              </p>
              <Link
                to="/blog"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4"
                onClick={() => setQuery('')}
              >
                Clear filters <X className="size-4" />
              </Link>
            </div>
          )}
        </Container>
      </section>

      <section className="border-t border-hairline bg-surface py-14 sm:py-16">
        <Container className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <p className="blog-eyebrow">YOUR NEXT STEP</p>
            <h2 className="mt-3 text-2xl font-medium tracking-tight sm:text-3xl">
              Bring your onchain operations into focus.
            </h2>
            <p className="mt-3 text-sm leading-6 text-ink-500">
              Talk through your wallet architecture and workflows with our team.
            </p>
          </div>
          <BookDemoButton
            size="lg"
            analyticsLocation="blog_footer"
            className="self-start sm:shrink-0"
          >
            Book a demo <ArrowRight className="size-4" />
          </BookDemoButton>
        </Container>
      </section>
    </>
  );
}

export function Blog() {
  const { category: categorySlug } = useParams<{ category?: string }>();
  const category = getBlogCategory(categorySlug);
  if (categorySlug && !category) return <NotFound />;
  return <BlogIndex key={category?.slug ?? 'all'} category={category} />;
}
